import { complement, either, isEmpty, isNil, keys, pathOr } from "ramda";

import { serialize } from "object-to-formdata";

export const sortOptions = [
  { name: "Sort By", value: "default" },
  { name: "Most Recent", value: "recent" },
  { name: "Active", value: "active" },
  { name: "Complete", value: "complete" },
  { name: "Alphabetical Order", value: "alphabet" },
];

export const sortCardData = (options) => {
  const { originalCardData, search, sort } = options;
  let newData = [];

  if (sort === "default") {
    newData = originalCardData.sort((a, b) => a.index - b.index);
  }
  if (sort === "active") {
    newData = originalCardData.filter((matchingGift) => matchingGift.active);
  }
  if (sort === "complete") {
    newData = originalCardData.filter((matchingGift) => matchingGift.complete);
  }
  if (sort === "recent") {
    newData = originalCardData.sort((a, b) =>
      `${a.start_date}`.localeCompare(`${b.start_date}`),
    );
  }
  if (sort === "alphabet") {
    newData = originalCardData.sort((a, b) => a.title.localeCompare(b.title));
  }

  newData = newData.filter((matchingGift) => {
    const currentTitle = matchingGift.title.toLowerCase();

    return currentTitle.includes(search.toLowerCase());
  });

  return newData;
};

export const defaultSort = "default";
export const hasValue = complement(either(isNil, isEmpty));
export const isEmptyValue = either(isNil, isEmpty);
export const pluckData = pathOr({}, ["data", "matching_gifts_section"]);

export const calculateMatchAmount = (value, type) => {
  if (type != "Participation Based" && type != "Donation Based") {
    if (type === "Dollar for Dollar") {
      return value * 2;
    }
    if (type === "2:1 Match") {
      return value * 3;
    }
  } else {
    return value;
  }
};

export const buildCampaignData = (
  currentFormObjectCampaignRegistration,
  currentFormObjectCampaignMedia,
  sectionId,
) => {
  const {
    descr,
    email,
    endDate: end_date,
    firstName: first_name,
    goal,
    isOngoing: is_ongoing,
    lastName: last_name,
    name,
    startDate: start_date,
  } = currentFormObjectCampaignRegistration;

  const {
    coverImage: banner,
    logoImage: logo,
    campaignImage1: campaign_image_1,
    campaignImage2: campaign_image_2,
    campaignImage3: campaign_image_3,
    campaignImage4: campaign_image_4,
  } = currentFormObjectCampaignMedia;

  const formData = new FormData();
  const campaignData = {
    first_name,
    last_name,
    email,
    name,
    descr,
    start_date,
    is_ongoing,
    goal,
    logo,
    banner,
  };

  if (!is_ongoing) {
    campaignData.end_date = end_date;
  }

  const campaignImageData = {
    campaign_image_1,
    campaign_image_2,
    campaign_image_3,
    campaign_image_4,
  };

  keys(campaignData).forEach((key) => {
    formData.append(`campaign[${key}]`, campaignData[key]);
  });

  keys(campaignImageData).forEach((key) => {
    if (campaignImageData[key]) {
      formData.append("campaign_images[]", campaignImageData[key]);
    }
  });

  formData.append("campaign[section_id]", sectionId);

  return formData;
};

export const centsToDollars = (cents) => {
  const dollarsAndCents = (cents / 100).toFixed(2);

  return Number(dollarsAndCents);
};

export const buildGivingDonationData = (
  entityId,
  currentFormObjectDonatePayment,
  currentFormObjectRegistration,
  matchingGiftsSectionId,
  paymentProcessType,
  dafWorkflowSessionId,
  dafAmount,
  spreedlyToken,
  stripePaymentIntent,
  stripeSetupIntent,
  braintreePaymentData,
  bbmsTransactionToken,
  selectedCampaigns,
  feeAmount,
  isGiftAid,
  subscriptionValues,
  causesAllocation,
  donationLevelId,
  dDCompanyId,
  stripeClientSecret = null,
  paypalDetails = null,
  everydayForm = null,
  resolvedCustomFields = null,
) => {
  const {
    firstName,
    lastName,
    address1,
    address2,
    city,
    state,
    country,
    zip,
    email,
    phone,
    displayName,
    displayType,
    hideAmount,
    comment,
    addedAdditionalGift,
    browser_info,
    currencyCode,
    isMobileDevice,
    ambassador_id,
    joint_donation,
  } = currentFormObjectDonatePayment;

  const donationData = [];

  selectedCampaigns.map((campaign) =>
    donationData.push({
      campaign_id: campaign.id,
      amount: campaign.amount,
      is_mobile_device: isMobileDevice,
      everyday_form_id: everydayForm?.id,
      joint_donation,
    }),
  );

  const customFields = Array.isArray(resolvedCustomFields)
    ? resolvedCustomFields
    : (() => {
        const uniqueCustomFields = new Map();

        for (const campaign of selectedCampaigns) {
          if (campaign.custom_fields) {
            for (const field of campaign.custom_fields) {
              uniqueCustomFields.set(field._id.$oid, field);
            }
          }
        }

        if (everydayForm?.entity_custom_fields) {
          for (const field of everydayForm.entity_custom_fields) {
            uniqueCustomFields.set(field._id.$oid, field);
          }
        }

        return Array.from(uniqueCustomFields.values());
      })();

  const isApplePay = paypalDetails && paypalDetails.paypal_payment_mode === "applepay";

  let userParams;

  if (isApplePay || paypalDetails) {
    userParams = {
      first_name: firstName || paypalDetails.paypal_first_name,
      last_name: lastName || paypalDetails.paypal_last_name,
      email: email || paypalDetails.paypal_email,
    };
  } else {
    userParams = {
      first_name: firstName,
      last_name: lastName,
      email,
    };
  }

  const addressData = {
    address1,
    address2,
    city,
    state,
    region: state,
    zip,
    country,
    cell_phone: phone,
  };

  const causesAllocationHash = {};

  // divide percentage among causes evenly.
  if (hasValue(causesAllocation)) {
    causesAllocation.map(
      (cause_id) => (causesAllocationHash[cause_id] = 100 / causesAllocation.length),
    );
  }

  const paymentParams = {
    added_centralized_gift: addedAdditionalGift,
    first_name: firstName,
    last_name: lastName,
    display_name: displayName,
    display_type: displayType,
    hide_amount: hideAmount,
    comment,
    currency_code: currencyCode,
    spreedly_cc_token: spreedlyToken,
    stripe_payment_intent_id: stripePaymentIntent?.id,
    stripe_setup_intent_id: stripeSetupIntent?.id,
    stripe_payment_status: stripePaymentIntent?.status,
    payment_process_type: paymentProcessType,
    payment_method: stripePaymentIntent?.payment_method,
    braintree_nonce: braintreePaymentData?.nonce,
    braintree_payment_method_type: braintreePaymentData?.type,
    bbms_transaction_token: bbmsTransactionToken,
    fee_amount: feeAmount,
    is_gift_aid: isGiftAid,
    address: addressData,
    causes_allocation_hash: causesAllocationHash,
    custom_fields_hash: {},
    browser_info,
    donation_level_id: donationLevelId,
    dd_company_id: dDCompanyId,
    daf_amount: dafAmount,
    daf_workflow_session_id: dafWorkflowSessionId,
    ambassador_id,
  };

  if (stripeClientSecret) {
    paymentParams.stripe_client_secret = stripeClientSecret;
  }

  if (paypalDetails) {
    paymentParams.paypal_details = {
      paypal_order_id: paypalDetails.paypal_order_id,
      paypal_payer_id: paypalDetails.paypal_payer_id,
      paypal_payer_email: paypalDetails.paypal_payer_email,
      paypal_payment_amount: paypalDetails.paypal_payment_amount,
      paypal_currency_code: paypalDetails.paypal_currency_code,
      paypal_capture_id: paypalDetails.paypal_capture_id,
      paypal_capture_status: paypalDetails.paypal_capture_status,
      paypal_order_status: paypalDetails.paypal_order_status,
      paypal_payment_mode: paypalDetails.paypal_payment_mode,
      paypal_email: paypalDetails.paypal_email,
      paypal_address: paypalDetails.paypal_address,
    };
  }

  let subscriptionData = {};

  if (subscriptionValues.isSubscription) {
    subscriptionData = {
      period_length: subscriptionValues.periodLength,
      interval: subscriptionValues.interval,
    };
  }

  let customFieldsHash = {};

  try {
    if (customFields.length > 0) {
      // Create a set of valid custom field names to check against
      const customFieldNames = new Set();

      // Helper function to collect all field names at any nesting level
      const collectFieldNames = (fields) => {
        if (!fields) return;

        fields.forEach((field) => {
          if (field.name) {
            customFieldNames.add(field.name);
          }

          // Check for nested fields in entity_custom_values
          if (field.entity_custom_values) {
            field.entity_custom_values.forEach((value) => {
              if (value.custom_field) {
                collectFieldNames([value.custom_field]);
              }
            });
          }
        });
      };

      // Collect all valid custom field names
      collectFieldNames(customFields);

      // Process the fields to handle nesting
      const processCustomFields = (fields) => {
        fields.forEach((field) => {
          const fieldName = field.name;

          // Only include fields that are defined as custom fields
          if (customFieldNames.has(fieldName)) {
            const fieldValue = currentFormObjectDonatePayment[fieldName];

            // If this field has a value in the form data, include it
            if (fieldValue !== undefined && fieldValue !== null && fieldValue !== "") {
              customFieldsHash[fieldName] = fieldValue;

              // If this field has entity_custom_values with potential nested fields
              if (field.entity_custom_values && field.entity_custom_values.length > 0) {
                // For single select fields
                if (!field.multiple) {
                  // Find the selected option
                  const selectedOption = field.entity_custom_values.find(
                    (option) => option.name === fieldValue,
                  );

                  // If the selected option has a nested custom field
                  if (selectedOption && selectedOption.custom_field) {
                    // Process the nested field
                    processCustomFields([selectedOption.custom_field]);
                  }
                }
                // For multi-select fields
                else if (field.multiple && Array.isArray(fieldValue)) {
                  // Process each selected option for potential nested fields
                  fieldValue.forEach((value) => {
                    const selectedOption = field.entity_custom_values.find(
                      (option) => option.name === value,
                    );

                    if (selectedOption && selectedOption.custom_field) {
                      processCustomFields([selectedOption.custom_field]);
                    }
                  });
                }
              }
            }
          }
        });
      };

      // Start processing from the top-level fields
      processCustomFields(customFields);
    }

  } catch (error) {
    console.error("Error building custom fields hash: ", error);
  }

  paymentParams.custom_fields_hash = JSON.stringify(customFieldsHash);

  let matchingGiftData = {};

  if (hasValue(currentFormObjectRegistration)) {
    matchingGiftData = {
      campaign_id: currentFormObjectRegistration.campaign.id || null,
      end_date: currentFormObjectRegistration.endDate || null,
      money_goal: currentFormObjectRegistration.moneyGoal || null,
      participation_goal: currentFormObjectRegistration.participationGoal || null,
      donation_goal: currentFormObjectRegistration.donationGoal || null,
      start_date: currentFormObjectRegistration.startDate || null,
      title: currentFormObjectRegistration.title || null,
      type: currentFormObjectRegistration.type || null,
      full_description: currentFormObjectRegistration.fullDescription || null,
      matching_gifts_section_id: matchingGiftsSectionId || null,
      image_filename: currentFormObjectRegistration.image || null,
      custom_field_id: currentFormObjectRegistration.customFieldId || null,
      custom_field_value_id: currentFormObjectRegistration.customFieldValueId || null,
      match_gift_amount: currentFormObjectRegistration.matchGiftAmount || null,
      match_gift_amount_op: currentFormObjectRegistration.matchGiftAmountOp || null,
    };
  }

  const clientRequest = {
    entity_id: entityId,
    user: userParams,
    payment: paymentParams,
    donations: donationData,
    subscription: subscriptionData,
    matching_gift: matchingGiftData,
  };

  const formData = serialize(clientRequest);

  return formData;
};
